import { Merge } from "lucide-react";
import { useDataProvider, useRefresh } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Contact, Deal } from "../types";

/**
 * Duplicate finder & merge.
 * Scans contacts for pairs sharing an email address or an identical full
 * name, then merges the newer record into the older: blank fields fill in,
 * emails/phones/tags union, and notes, tasks, and deals move to the
 * surviving contact before the duplicate is removed.
 */

interface DupPair {
  keep: Contact;
  drop: Contact;
  reason: "email" | "name";
}

const emailsOf = (c: Contact) =>
  (c.email_jsonb ?? []).map((e) => e.email.toLowerCase()).filter(Boolean);

export const DuplicatesButton = () => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [pairs, setPairs] = useState<DupPair[]>([]);
  const [scanning, setScanning] = useState(false);
  const [merged, setMerged] = useState(0);

  const scan = async () => {
    setScanning(true);
    try {
      const { data: contacts } = await dataProvider.getList<Contact>("contacts", {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: "first_seen", order: "ASC" },
        filter: {},
      });
      const found: DupPair[] = [];
      const claimed = new Set<string | number>();
      const byEmail = new Map<string, Contact>();
      const byName = new Map<string, Contact>();
      for (const c of contacts) {
        let matched = false;
        for (const e of emailsOf(c)) {
          const prior = byEmail.get(e);
          if (prior && !claimed.has(c.id) && !claimed.has(prior.id)) {
            found.push({ keep: prior, drop: c, reason: "email" });
            claimed.add(c.id);
            matched = true;
            break;
          }
          if (!prior) byEmail.set(e, c);
        }
        if (matched) continue;
        const name = `${c.first_name} ${c.last_name}`.trim().toLowerCase();
        if (name.length > 1) {
          const prior = byName.get(name);
          if (prior && !claimed.has(c.id) && !claimed.has(prior.id)) {
            found.push({ keep: prior, drop: c, reason: "name" });
            claimed.add(c.id);
          } else if (!prior) {
            byName.set(name, c);
          }
        }
      }
      setPairs(found);
      setMerged(0);
      setOpen(true);
    } finally {
      setScanning(false);
    }
  };

  const merge = async (pair: DupPair) => {
    const { keep, drop } = pair;

    // 1. Merge record fields: keep wins, blanks fill from drop; unions for arrays
    const emailSeen = new Set(emailsOf(keep));
    const mergedRecord: Partial<Contact> & Record<string, unknown> = {
      email_jsonb: [
        ...(keep.email_jsonb ?? []),
        ...(drop.email_jsonb ?? []).filter(
          (e) => !emailSeen.has(e.email.toLowerCase()),
        ),
      ],
      phone_jsonb: [
        ...(keep.phone_jsonb ?? []),
        ...(drop.phone_jsonb ?? []).filter(
          (p) => !(keep.phone_jsonb ?? []).some((k) => k.number === p.number),
        ),
      ],
      tags: [...new Set([...(keep.tags ?? []), ...(drop.tags ?? [])])],
    };
    for (const [k, v] of Object.entries(drop)) {
      if (["title", "linkedin_url", "background", "company_id"].includes(k)) {
        const current = (keep as Record<string, unknown>)[k];
        if ((current == null || current === "") && v != null && v !== "")
          mergedRecord[k] = v;
      }
    }
    // custom_fields is a jsonb object, not flat custom_<key> properties -
    // merge key by key with the same blanks-fill-from-drop rule above.
    const mergedCustomFields = { ...(keep.custom_fields ?? {}) };
    for (const [k, v] of Object.entries(drop.custom_fields ?? {})) {
      const current = mergedCustomFields[k];
      if ((current == null || current === "") && v != null && v !== "")
        mergedCustomFields[k] = v;
    }
    if (Object.keys(mergedCustomFields).length > 0) {
      mergedRecord.custom_fields = mergedCustomFields;
    }
    await dataProvider.update("contacts", {
      id: keep.id,
      data: mergedRecord,
      previousData: keep,
    });

    // 2. Reassign notes and tasks
    for (const resource of ["contactNotes", "tasks"]) {
      const { data } = await dataProvider.getList(resource, {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "id", order: "ASC" },
        filter: { contact_id: drop.id },
      });
      for (const rec of data) {
        await dataProvider.update(resource, {
          id: rec.id,
          data: { contact_id: keep.id },
          previousData: rec,
        });
      }
    }

    // 3. Repoint deals that referenced the duplicate
    const { data: deals } = await dataProvider.getList<Deal>("deals", {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
      filter: {},
    });
    for (const deal of deals) {
      if ((deal.contact_ids ?? []).includes(drop.id)) {
        await dataProvider.update("deals", {
          id: deal.id,
          data: {
            contact_ids: [
              ...new Set(
                deal.contact_ids.map((id) => (id === drop.id ? keep.id : id)),
              ),
            ],
          },
          previousData: deal,
        });
      }
    }

    // 4. Remove the duplicate
    await dataProvider.delete("contacts", { id: drop.id, previousData: drop });

    setPairs((p) => p.filter((x) => x !== pair));
    setMerged((m) => m + 1);
    refresh();
  };

  return (
    <>
      <Button variant="outline" onClick={scan} disabled={scanning}>
        <Merge /> {scanning ? "Scanning…" : "Find duplicates"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Duplicate contacts</DialogTitle>
            <DialogDescription>
              {pairs.length === 0
                ? merged > 0
                  ? `All clean - ${merged} pair${merged === 1 ? "" : "s"} merged.`
                  : "No duplicates found by email or name."
                : `${pairs.length} likely duplicate pair${pairs.length === 1 ? "" : "s"}. Merging keeps the older record, fills its blanks, and moves notes, tasks, and deals across.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            {pairs.map((pair) => (
              <div
                key={`${pair.keep.id}-${pair.drop.id}`}
                className="flex items-center gap-3 border border-border rounded p-3"
              >
                <div className="flex-1 text-sm">
                  <p className="font-medium">
                    {pair.keep.first_name} {pair.keep.last_name}
                    <span className="text-muted-foreground font-normal"> ← keeps</span>
                  </p>
                  <p className="text-muted-foreground">
                    {pair.drop.first_name} {pair.drop.last_name} · matched by{" "}
                    {pair.reason}
                  </p>
                </div>
                <Button size="sm" onClick={() => merge(pair)}>
                  Merge
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
